import { Radio, Switch } from 'antd';
import _ from 'lodash';
import { CheckboxInput } from '@/shared/ui';
import { IEventFilter } from '@/shared/model';
import {
  optionsSortDuration,
  optionsSortEventsCountBeats,
  optionsSortEventsRhythm,
} from '@/entities/event';
import useFilterPopover from './useFilterPopover';

export type FilterPopoverProps = {
  eventFilter: IEventFilter;
  setEventFilter: (eventFilter: IEventFilter) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  setPageIndex: (page: any) => void;
  keyRecoil: string;
  isShowCountBeats?: boolean;
  isShowFilterDuration?: boolean;
  optionrhythm?: boolean;
  isShowAvgHrBelow100?: boolean;
};

/**
 * Renders the ECG events filter and sort popover for the active event tab. Reads the current filter values and handlers from useFilterPopover, shows the hide-reviewed switch with the captured-strips and low-HR checkboxes, and picks one of three sort option sets depending on whether beat counts, durations only or rhythm averages apply, closing with reset and apply buttons.
 *
 * @param props - Popover props carrying keyRecoil and the flags selecting which filter and sort controls apply.
 * @returns The rendered filter popover.
 */
const FilterPopover = ({
  eventFilter,
  setEventFilter,
  isOpen,
  setIsOpen,
  setPageIndex,
  keyRecoil,
  isShowCountBeats,
  isShowFilterDuration,
  optionrhythm,
  isShowAvgHrBelow100,
}: FilterPopoverProps) => {
  const {
    isHideReviewed,
    isCapture,
    isAvgHrBelow100,
    sortOrder,
    onChangeSortEvents,
    onSelectSortOption,
    onChangeSwitch,
    onChangeCheckbox,
    onClickReset,
    onClickApply,
  } = useFilterPopover({
    eventFilter,
    setEventFilter,
    isOpen,
    setIsOpen,
    setPageIndex,
    isShowCountBeats,
    isShowFilterDuration,
    optionrhythm,
  });

  return (
    <div className="filter-popover">
      <div className="switch-review">
        <div id="switch-review-label" className="switch-review-label">
          Hide reviewed events
        </div>
        <div className="switch-button">
          <span className="switch-buton-text">{isHideReviewed ? 'On' : 'Off'}</span>
          <Switch
            aria-labelledby="switch-review-label"
            checked={isHideReviewed}
            onChange={onChangeSwitch}
          />
        </div>
      </div>
      <div className="filter-popover-divider" />
      <div className="event-attributes">
        <div className="event-attributes-label">Filter by event attributes</div>
        <CheckboxInput
          id={`capturedStrip${keyRecoil}`}
          name="capturedStrip"
          data={{
            label: 'Captured strips',
            value: isCapture as unknown as string, // pre-existing: CheckboxInput's data.value is string|number, this passes a boolean
          }}
          checked={isCapture}
          onChange={onChangeCheckbox}
        />
        {isShowAvgHrBelow100 && (
          <CheckboxInput
            id={`AvgHrBelow100${keyRecoil}`}
            name="AvgHrBelow100"
            data={{
              label: 'HR < 100 bpm',
              value: isAvgHrBelow100 as unknown as string, // pre-existing: CheckboxInput's data.value is string|number, this passes a boolean
            }}
            checked={isAvgHrBelow100}
            onChange={onChangeCheckbox}
          />
        )}
      </div>
      {isShowCountBeats ? (
        <>
          <div className="filter-popover-divider" />
          <div className="sort-events-by">
            <div className="sort-events-by-label">Sort events by</div>
            <Radio.Group value={sortOrder}>
              {optionsSortEventsCountBeats.map((option) => (
                <Radio
                  key={`sort-events-${option}`}
                  onClick={() => {
                    onSelectSortOption(option);
                  }}
                  value={option}
                >
                  {_.isNil(option) && 'None'}
                  {option === 'durAsc' && 'Event duration: ascending'}
                  {option === 'durDesc' && 'Event duration: descending'}
                  {option === 'asc' && 'Beat count: ascending'}
                  {option === 'desc' && 'Beat count: descending'}
                  {option === 'hrAsc' && 'Average HR: ascending'}
                  {option === 'hrDesc' && 'Average HR: descending'}
                </Radio>
              ))}
            </Radio.Group>
          </div>
        </>
      ) : isShowFilterDuration ? (
        <>
          <div className="filter-popover-divider" />
          <div className="sort-events-by">
            <div className="sort-events-by-label">Sort events by</div>
            <Radio.Group value={sortOrder}>
              {optionsSortDuration.map((option, index) => (
                <Radio
                  key={`sort-events-${option}`}
                  onClick={() => {
                    onChangeSortEvents(index, true);
                  }}
                  value={option}
                >
                  {_.isNil(option) && 'None'}
                  {option === 'asc' && 'Event duration: ascending'}
                  {option === 'desc' && 'Event duration: descending'}
                </Radio>
              ))}
            </Radio.Group>
          </div>
        </>
      ) : (
        optionrhythm && (
          <>
            <div className="filter-popover-divider" />
            <div className="sort-events-by">
              <div className="sort-events-by-label">Sort events by</div>
              <Radio.Group value={sortOrder}>
                {optionsSortEventsRhythm.map((option) => (
                  <Radio
                    key={`sort-events-${option}`}
                    onClick={() => {
                      onSelectSortOption(option);
                    }}
                    value={option}
                  >
                    {_.isNil(option) && 'None'}
                    {option === 'asc' && 'Event duration: ascending'}
                    {option === 'desc' && 'Event duration: descending'}
                    {option === 'hrAsc' && 'Average HR: ascending'}
                    {option === 'hrDesc' && 'Average HR: descending'}
                  </Radio>
                ))}
              </Radio.Group>
            </div>
          </>
        )
      )}
      <div className="filter-popover-divider" />
      <div className="filter-popover-footer">
        <button
          id="ecg-events-reset-filter-popover-btn"
          className="filter-popover-button"
          onClick={onClickReset}
        >
          Reset
        </button>
        <button
          id="ecg-events-apply-filter-popoverbtn"
          className="filter-popover-button --apply"
          onClick={onClickApply}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default FilterPopover;
